import {
  hasPermission,
  type RundownPermission,
} from '~shared/rundown/permission'

import { RundownToken } from '../../../axios'

/**
 * Check if the current user/guest has a specific permission.
 *
 * @remarks
 *   Plain function, not a hook: reads the RundownToken at call time, so it is
 *   safe in helpers and event handlers. False until the token is initialized.
 *
 * @param permission - The RundownPermission flag to check
 */
export function hasRundownPermission(permission: RundownPermission): boolean {
  const permissions = RundownToken.permissions
  if (typeof permissions !== 'number') return false
  return hasPermission(permissions, permission)
}

/**
 * Hook to check rundown permissions using RundownToken as the single source of
 * truth.
 *
 * The backend determines all permissions (team role, event membership, guest
 * access) and encodes them in the RundownToken JWT. This hook simply reads that
 * result.
 *
 * @example
 *   ;```tsx
 *   const { isReady, hasPermission, canEdit } = useRundownAccess()
 *
 *   if (!isReady) return <Loading />
 *   if (!canEdit) return <ReadOnlyView />
 *   ```
 *
 * @returns Object with isReady state and permission check methods
 */
export const useRundownAccess = () => {
  const permissions = RundownToken.permissions

  /**
   * Whether the RundownToken has been initialized with permissions.
   * Components should check this before rendering permission-dependent content.
   */
  const isReady = typeof permissions === 'number'

  return {
    isReady,
    /** See {@link hasRundownPermission}. */
    hasPermission: hasRundownPermission,
    /**
     * Raw permission bitmask from the token.
     * Prefer using hasPermission() for checks.
     */
    permissions,
  }
}
